'use client'

import { useEffect } from 'react'
import { cx } from '@/app/lib/utils'
import { IconClose } from '@/app/components/atoms/Icons'
import styles from './Toast.module.scss'
import type { ToastProps } from './types'
import {
  TOAST_CLOSE_LABEL,
  TOAST_DEFAULT_VARIANT,
  TOAST_STATUS_ICONS,
  TOAST_STATUS_LABELS,
} from './constants'

/**
 * Toast — notificación transitoria del DS Entradas.
 *
 * Comunica el resultado de una acción (success / error / info / warning) con un
 * icono de estado, un mensaje y, opcionalmente, un botón de cierre y/o un
 * temporizador de auto-cierre.
 *
 * Accesibilidad:
 * - La variante `error` usa `role="alert"` (aria-live assertive) por ser
 *   crítica; el resto usa `role="status"` (aria-live polite).
 * - El estado nunca se transmite solo por color: se acompaña de un icono y de
 *   un texto de estado oculto visualmente para lectores de pantalla.
 * - El botón de cierre expone `aria-label`.
 *
 * Client Component: gestiona el temporizador de auto-cierre (`useEffect`) y el
 * handler de cierre (`onClose`).
 */
export function Toast({
  message,
  variant = TOAST_DEFAULT_VARIANT,
  closable = false,
  onClose,
  duration = 0,
  className,
}: ToastProps) {
  const isError = variant === 'error'
  const StatusIcon = TOAST_STATUS_ICONS[variant]

  useEffect(() => {
    if (duration <= 0) return

    const timer = setTimeout(() => onClose?.(), duration)
    return () => clearTimeout(timer)
  }, [duration, onClose])

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={cx(styles.toast, styles[variant], className)}
    >
      <span className={styles.icon} aria-hidden="true">
        <StatusIcon size={20} />
      </span>

      <p className={styles.message}>
        <span className={styles.statusLabel}>{TOAST_STATUS_LABELS[variant]}: </span>
        {message}
      </p>

      {closable && (
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => onClose?.()}
          aria-label={TOAST_CLOSE_LABEL}
        >
          <IconClose size={16} />
        </button>
      )}
    </div>
  )
}
